import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { UsersListQuery } from '@rossum/api-client/users';
import { useUsers } from '../../../../business/users/useUsers';
import { getRoleId } from '../../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../../redux/modules/groups/selectors';
import {
  isUserAdmin,
  userSelector,
} from '../../../../redux/modules/user/selectors';

/** Count users matching a filter without paging them in. */
const useMatchCount = (query: UsersListQuery, enabled: boolean) => {
  const { data, isFetching } = useUsers(
    { deleted: false, pageSize: 1, page: 1, ...query },
    { enabled }
  );
  return { total: data?.pagination.total ?? 0, isFetching };
};

type UseManageableUsersCountParams = {
  searchValue: string;
  /** Total users matching the current filter (from the list query). */
  totalUsers: number;
  /** Whether the list query is currently fetching. */
  usersDataFetching: boolean;
};

/**
 * Number of users the batch Enable/Disable All buttons actually operate on and
 * whether those buttons should be shown.
 *
 * Admins have access via their role and the current user cannot manage their
 * own access — both have disabled toggles — so neither is counted.
 */
export const useManageableUsersCount = ({
  searchValue,
  totalUsers,
  usersDataFetching,
}: UseManageableUsersCountParams) => {
  const groups = useSelector(groupsSelector);
  const currentUserUrl = useSelector(userSelector).url;
  const isCurrentUserAdmin = useSelector(isUserAdmin);

  const adminGroupId = getRoleId('admin', groups);
  const orgGroupAdminGroupId = getRoleId('organization_group_admin', groups);

  // Admins and the current user can't be batch-toggled, so both are excluded
  // from the count. Admin current users are already in `admins`, so the self
  // query only runs for a non-admin current user.
  const admins = useMatchCount(
    {
      search: searchValue || undefined,
      groups:
        adminGroupId && orgGroupAdminGroupId
          ? [adminGroupId, orgGroupAdminGroupId]
          : [],
    },
    !!adminGroupId && !!orgGroupAdminGroupId && !!searchValue
  );
  const self = useMatchCount(
    { search: searchValue || undefined, id: getIDFromUrl(currentUserUrl) },
    !!searchValue && !isCurrentUserAdmin
  );

  const adminsCount = searchValue ? admins.total : 0;
  const selfCount =
    searchValue && !isCurrentUserAdmin && self.total > 0 ? 1 : 0;
  const manageableCount = Math.max(totalUsers - adminsCount - selfCount, 0);

  const fetching = admins.isFetching || self.isFetching || usersDataFetching;
  return {
    batchButtonsDisabled: manageableCount === 0,
    count: searchValue && !fetching ? manageableCount : 0,
  };
};
